import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../../lib/api-client';

import type { ChannelConfigWire, ChannelHealthWire, ChannelTestWire } from './types';

/**
 * Hooks da aba «Canais» (F2 §4.3).
 *
 * `staleTime` curto na saúde (10s) de propósito: esta tela é aberta **quando algo está
 * errado**, e um dado de meia hora atrás é pior que nenhum — dá a impressão de que o
 * conserto não pegou.
 */

export const CHANNELS_KEY = ['admin', 'integrations', 'channels'] as const;

export function channelHealthKey(id: string): readonly string[] {
  return [...CHANNELS_KEY, 'health', id];
}

export function useChannelConfigs(): ReturnType<typeof useQuery<ChannelConfigWire[]>> {
  return useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: () => api.listChannelConfigs(),
    staleTime: 30_000,
  });
}

export function useChannelHealth(id: string): ReturnType<typeof useQuery<ChannelHealthWire>> {
  return useQuery({
    queryKey: channelHealthKey(id),
    queryFn: () => api.getChannelHealth(id),
    staleTime: 10_000,
  });
}

export function useTestChannel(): ReturnType<typeof useMutation<ChannelTestWire, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.testChannelConnection(id),
    // O teste GRAVA `lastTestAt`/`lastTestResult`, então a saúde envelheceu no mesmo
    // instante. Sem esta invalidação a tela mostraria o resultado novo no toast e o
    // antigo no cartão — duas verdades ao mesmo tempo.
    onSuccess: (_r, id) => void qc.invalidateQueries({ queryKey: channelHealthKey(id) }),
  });
}

export function useWriteChannelCredential(): ReturnType<
  typeof useMutation<
    { credentialRotatedAt: string; version: number },
    Error,
    { id: string; version: number; secret: string }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, secret }) => api.writeChannelCredential(id, version, secret),
    onSuccess: (_r, { id }) => {
      // Duas invalidações, e as duas importam: a lista porque a `version` do config
      // subiu (o próximo If-Match usaria a velha e levaria 409), e a saúde porque
      // trocar credencial ZERA o último teste no backend.
      void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      void qc.invalidateQueries({ queryKey: channelHealthKey(id) });
    },
  });
}

export function useSetChannelEnabled(): ReturnType<
  typeof useMutation<ChannelConfigWire, Error, { id: string; version: number; enabled: boolean }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, enabled }) => api.setChannelEnabled(id, version, enabled),
    onSuccess: (_r, { id }) => {
      // As duas, pelo mesmo motivo do write de credencial: a `version` subiu (próximo
      // If-Match levaria 409) e a saúde mudou de estado no mesmo instante — `connected`
      // depende do interruptor.
      void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      void qc.invalidateQueries({ queryKey: channelHealthKey(id) });
    },
  });
}
